import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  addAnimationItemAction,
  deleteAnimationItemAction,
  resetAnimationAction,
  setCurrentAnimationId,
  setCurrentAnimationIdBase,
  setAnimationSubValue,
  requestSpeechFileFromText,
  setAnimationControlPanelState,
  requestSpeechFileFromTextRequest,
  setAnimationEase,
  setAnimationEaseBase,
  addAnimationsOfShapesToStoreBase,
  setAnimationItemAction,
} from './animation.actions';
import {
  selectSelectedShapes,
  selectedShapes,
} from '../../store/selector/editor.selector';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AnimationService } from '../animation.service';
import { selectCurrentAnimationId } from './animation.selector';
import {
  resetBaseState,
  setChangeTookPlace,
  setResourcePatch,
} from '../../store/editor.actions';
import { HttpService } from '../../../store/http/http.service';
import { RequestType } from '../../../store/actions';
import { ShapeIRI } from '../../../elements/resource/types/shape.type';
import { Animations } from './animation.reducer';
import { set } from 'lodash';

@Injectable()
export class AnimationEffects {
  requestSpeechFileFromText$: Observable<Action>;
  requestSpeechFileFromTextRequest$: Observable<Action>;
  addAnimationItem$: Observable<Action>;
  addAnimationItemForShape$: Observable<Action>;
  removeAnimationItem$: Observable<Action>;
  deleteAnimation$: Observable<Action>;
  setCurrentAnimationId$: Observable<Action>;
  initAnimationsFile$: Observable<Action>;
  setMainAnimationFrame$: Observable<Action>;
  setAnimationSubValue$: Observable<Action>;
  addAnimationsOfShapesToStore$: Observable<Action>;
  addTranslateAnimation$: Observable<Action>;
  addScaleAnimation$: Observable<Action>;
  addRotationAnimation$: Observable<Action>;
  setAnimationEase$: Observable<Action>;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly animationService: AnimationService,
    private readonly http: HttpService,
  ) {
    this.setAnimationEase$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setAnimationEase),
        withLatestFrom(this.store.select(selectCurrentAnimationId)),
        switchMap(([{ shapeIRI, key, ease }, animationId]) => [
          setAnimationEaseBase({ shapeIRI, key, ease, animationId }),
        ]),
      ),
    );

    this.requestSpeechFileFromText$ = createEffect(() =>
      this.actions$.pipe(
        ofType(requestSpeechFileFromText),
        switchMap(({ text, id }) => [
          requestSpeechFileFromTextRequest({ text, id }),
          setAnimationControlPanelState({
            state: {
              mode: 'text-frame-form',
              isLoading: true,
            },
          }),
          // setAnimationControlPanelState({
          //   state: {
          //     mode: 'generated',
          //     isLoading: false,
          //     speechFile: {
          //       url: '123.mp3',
          //       sizeInKb: 20,
          //     },
          //   },
          // }),
        ]),
      ),
    );

    this.requestSpeechFileFromTextRequest$ = createEffect(() =>
      this.actions$.pipe(
        ofType(requestSpeechFileFromTextRequest),
        switchMap(({ text, id }) => {
          return this.http.requestCall({
            path: '/audio/text-to-speech',
            type: RequestType.POST,
            body: {
              text,
              id: Math.random().toString(),
              voice: 'echo',
            },
          });
        }),
        switchMap(({ filename, size }) => {
          return [
            setAnimationControlPanelState({
              state: {
                mode: 'text-frame-form',
                isLoading: false,
                speechFile: {
                  filename,
                  size,
                },
              },
            }),
          ];
        }),
      ),
    );

    this.addAnimationItem$ = createEffect(() =>
      this.actions$.pipe(
        // this action is coming from the attribute-panel //
        ofType(addAnimationItemAction),
        withLatestFrom(this.store.select(selectCurrentAnimationId)),
        switchMap(([{ item, IRI, IRIs }, currentAnimationId]) => {
          const shapeIRIs = IRI ? [IRI] : IRIs ? IRIs : [];
          return [
            addAnimationsOfShapesToStoreBase({
              animations: shapeIRIs.reduce((object, IRI) => {
                object[IRI] = {
                  [currentAnimationId]: {
                    [item.key]: item,
                  },
                };
                return object;
              }, {}),
            }),
            setResourcePatch({
              IRIs: shapeIRIs,
              // resourceType: 'nw:Shape',
              field: 'literals',
              key: 'descriptor',
            }),
            setChangeTookPlace(),
          ];
        }),
        catchError(error => {
          return [];
        }),
      ),
    );

    this.setAnimationSubValue$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setAnimationSubValue),
        withLatestFrom(
          this.store.select(selectCurrentAnimationId),
          this.store.select(selectSelectedShapes),
        ),
        switchMap(
          ([
            { IRIs, IRI, animationKey, innerKey, value },
            currentAnimationId,
            selectedShapes,
          ]) => {
            const shapeIRIs = IRI
              ? [IRI]
              : IRIs
                ? IRIs
                : Object.keys(selectedShapes);

            const animations = shapeIRIs.reduce((object, IRI) => {
              object[IRI] = {
                [currentAnimationId]: {
                  [animationKey]: innerKey
                    ? {
                        [innerKey]: value,
                      }
                    : value,
                },
              };
              return object;
            }, {});

            console.log('setAnimationSubValue', animations);
            return [
              setAnimationItemAction({
                IRIs: shapeIRIs,
                animationId: currentAnimationId,
                key: animationKey,
                innerKey,
                value,
              }),
              addAnimationsOfShapesToStoreBase({
                animations,
              }),
              // setResourcePatch({
              //   IRIs: shapeIRIs,
              //   resourceType: 'nw:Shape',
              //   field: 'literals',
              //   key: 'descriptor',
              // }),
              // setChangeTookPlace(),
            ];
          },
        ),
      ),
    );

    this.deleteAnimation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(deleteAnimationItemAction),
        withLatestFrom(this.store.select(selectedShapes)),
        map(([{ animationKey }, selectedShapes]) =>
          resetAnimationAction({
            IRIs: Object.keys(selectedShapes),
            key: animationKey,
          }),
        ),
      ),
    );

    this.setCurrentAnimationId$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setCurrentAnimationId),
        switchMap(({ id }) => {
          // if (!id) {
          //   return [resetBaseState()];
          // }

          return [
            setCurrentAnimationIdBase({ id }),
            !id ? resetBaseState() : undefined,
          ].filter(v => !!v);
        }),
        catchError(error => {
          console.log({ error });
          return of(error.message);
        }),
      ),
    );
  }
}
